import type { ComponentType, KeyboardEvent } from 'react';
import { type MessageKey, useI18n } from '../i18n';
import { Link, navigate, useTitle } from '../router';
import { SafetyPanel } from '../components/ui';
import { TitrationSim } from './lab/TitrationSim';
import { ConcentrationSim } from './lab/ConcentrationSim';
import { DilutionSim } from './lab/DilutionSim';
import { BalancerTool } from './lab/BalancerTool';
import { GeometryLab } from './lab/GeometryLab';
import { AtomSim } from './lab/AtomSim';
import { GasSim } from './lab/GasSim';
import { RelatedPhet } from './SimulationsPage';
import { RELATED_PHET } from '../lib/phet';
import { LAB_EXPERIMENTS, LAB_TOPICS } from '../lib/labExperiments';

export const SIMS = ['titration', 'concentration', 'dilution', 'balancer', 'geometry', 'atom', 'gas'] as const;
export type SimId = (typeof SIMS)[number];

const VIEWS: Record<SimId, ComponentType> = {
  titration: TitrationSim,
  concentration: ConcentrationSim,
  dilution: DilutionSim,
  balancer: BalancerTool,
  geometry: GeometryLab,
  atom: AtomSim,
  gas: GasSim,
};

const NEW_LAB = {
  en: { title: 'Virtual chemistry lab', text: `${LAB_EXPERIMENTS.length} guided experiments in ${LAB_TOPICS.length} topics, including organic chemistry: mix, heat, test gases and watch reactions happen.`, cta: 'Open the lab' },
  ro: { title: 'Laborator virtual de chimie', text: `${LAB_EXPERIMENTS.length} de experimente ghidate în ${LAB_TOPICS.length} teme, inclusiv chimie organică: amestecă, încălzește, testează gaze și urmărește reacțiile.`, cta: 'Deschide laboratorul' },
  tr: { title: 'Sanal kimya laboratuvarı', text: `${LAB_TOPICS.length} konuda, organik kimya dahil ${LAB_EXPERIMENTS.length} rehberli deney: karıştır, ısıt, gazları test et ve tepkimeleri izle.`, cta: 'Laboratuvarı aç' },
} as const;

function NewLabBanner({ lang }: { lang: string }) {
  const L = NEW_LAB[lang as keyof typeof NEW_LAB] ?? NEW_LAB.en;
  return (
    <a href={`/chemlab/?lang=${lang}`}
      style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px 20px', margin: '0 0 20px', padding: '16px 20px',
        borderRadius: 14, background: 'linear-gradient(120deg, #0d141c, #16303a)', color: '#e7eef5', textDecoration: 'none',
        border: '1px solid #0d7068', boxShadow: '0 6px 24px rgba(13,112,104,.18)' }}>
      <span aria-hidden="true" style={{ fontSize: 34, lineHeight: 1 }}>🧪</span>
      <span style={{ flex: '1 1 260px', minWidth: 0 }}>
        <strong style={{ display: 'block', fontSize: 18, marginBottom: 2 }}>{L.title}</strong>
        <span style={{ fontSize: 14, color: '#b9c8d6' }}>{L.text}</span>
      </span>
      <span style={{ background: '#34d3bd', color: '#062a25', fontWeight: 700, padding: '10px 16px', borderRadius: 10, whiteSpace: 'nowrap' }}>{L.cta} →</span>
    </a>
  );
}

export function LabPage({ sim }: { sim?: string }) {
  const { t, lang } = useI18n();
  useTitle(t('lab.title'));
  const active: SimId = (SIMS as readonly string[]).includes(sim ?? '') ? (sim as SimId) : 'titration';
  const View = VIEWS[active];

  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const i = SIMS.indexOf(active);
    if (e.key === 'ArrowRight') navigate(`/lab/${SIMS[(i + 1) % SIMS.length]}`, { replace: true });
    if (e.key === 'ArrowLeft') navigate(`/lab/${SIMS[(i - 1 + SIMS.length) % SIMS.length]}`, { replace: true });
  };

  return (
    <div className="page">
      <header className="page-head">
        <h1>{t('lab.title')}</h1>
        <p className="lead">{t('lab.lead')}</p>
      </header>
      <NewLabBanner lang={lang} />
      <div className="tabs" role="tablist" aria-label={t('lab.title')} onKeyDown={onKey}>
        {SIMS.map((id) => (
          <Link key={id} to={`/lab/${id}`} role="tab" aria-selected={id === active} tabIndex={id === active ? 0 : -1}
            className={id === active ? 'tab on' : 'tab'}>
            {t(`lab.sim.${id}` as MessageKey)}
          </Link>
        ))}
      </div>
      <div role="tabpanel" aria-label={t(`lab.sim.${active}` as MessageKey)}>
        <View />
      </div>
      <RelatedPhet slugs={RELATED_PHET[active] ?? []} />
      <SafetyPanel />
    </div>
  );
}
